import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../../../services/apiFetch';
import ErrorAlert from '../../components/ErrorAlert';
import '../../../styles/LeagueForm.css';

const LeagueForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [formData, setFormData] = useState({
        name: '',
        image: ''
    });
    const [loading, setLoading] = useState(isEditing);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState(null);

    useEffect(() => {
        const fetchLeague = async () => {
            if (isEditing) {
                try {
                    const { data, error } = await apiGet(`/api/rl/leagues/${id}`);
                    if (error) throw new Error(error.error);
                    const leagueData = data.league;
                    setFormData({
                        name: leagueData.name,
                        image: leagueData.image
                    });
                    setLoading(false);
                } catch (error) {
                    console.error("Error fetching league:", error);
                    setLoading(false);
                }
            }
        };

        fetchLeague();
    }, [id, isEditing]);

    const validateForm = () => {
        const newErrors = {};

        // Validate league name
        if (!formData.name.trim()) {
            newErrors.name = "League name is required";
        }

        // Image is optional, no validation needed

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setApiError(null);

        // Validate form before submission
        if (!validateForm()) {
            return; // Stop form submission if validation fails
        }

        try {
            if (isEditing) {
                const { error } = await apiPut(`/api/rl/leagues/${id}`, {
                    id: id,
                    name: formData.name,
                    image: formData.image
                });
                if (error) {
                    setApiError(error);
                    return;
                }
                console.log("league updated");
            } else {
                const { error } = await apiPost('/api/rl/leagues', formData);
                if (error) {
                    setApiError(error);
                    return;
                }
                console.log("league created");
            }
            navigate('/admin/leagues');
        } catch (error) {
            console.error("Error submitting league:", error);
            setApiError({
                error: 'internal_error',
                message: error.message || 'Error submitting league. Please try again.',
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user types
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    if (loading) {
        return <div className="loading-spinner"></div>;
    }

    return (
        <div className="league-form-page">
            <div className="page-header">
                <button type="button" className="back-button" onClick={() => navigate(-1)} style={{marginLeft: 16}}>
                  ← Back
                </button>
                <h1>{isEditing ? 'Edit League' : 'Create League'}</h1>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-section">
                    <h2>League Details</h2>

                    <div className="form-row">
                        <div className="form-group">
                            <label>League Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={errors.name ? "error-input" : ""}
                            />
                            {errors.name && <div className="error-message">{errors.name}</div>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Image URL</label>
                            <input
                                type="text"
                                name="image"
                                value={formData.image}
                                onChange={handleInputChange}
                                placeholder="https://example.com/image.png"
                            />
                        </div>
                    </div>
                </div>

                <ErrorAlert 
                    error={apiError} 
                    onDismiss={() => setApiError(null)}
                    title="Error Submitting League"
                />

                <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="cancel-button">
                        Cancel
                    </button>
                    <button type="submit" className="submit-button">
                        {isEditing ? 'Update League' : 'Create League'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default LeagueForm;
